import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCookieConsent } from '../../hooks/useCookieConsent';
import Modal from './Modal';
import Button from './Button';

const CookieConsentBanner: React.FC = () => {
    const { t } = useTranslation();
    const { showBanner, acceptAll, rejectAll, saveConsent } = useCookieConsent();
    const [showPreferences, setShowPreferences] = useState(false);
    const [preferences, setPreferences] = useState({
        analytics: false,
        marketing: false,
    });

    if (!showBanner) return null;

    const handleSavePreferences = () => {
        saveConsent({
            necessary: true,
            analytics: preferences.analytics,
            marketing: preferences.marketing,
        });
        setShowPreferences(false);
    };

    return (
        <>
            {/* Main Banner */}
            <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-dark-bgSecondary border-t border-neutral-200 dark:border-dark-border p-4 shadow-lg z-50 animate-slide-up">
                <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
                    <div className="flex-1">
                        <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-2">
                            {t('cookies.title')}
                        </h3>
                        <p className="text-sm text-neutral-600 dark:text-dark-textSecondary">
                            {t('cookies.description')}
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <Button variant="secondary" onClick={() => setShowPreferences(true)}>
                            {t('common.customize')}
                        </Button>
                        <Button variant="secondary" onClick={rejectAll}>
                            {t('cookies.rejectAll')}
                        </Button>
                        <Button variant="primary" onClick={acceptAll}>
                            {t('cookies.acceptAll')}
                        </Button>
                    </div>
                </div>
            </div>

            {/* Preferences Modal */}
            <Modal
                isOpen={showPreferences}
                onClose={() => setShowPreferences(false)}
                title={t('cookies.preferencesTitle')}
                footer={
                    <div className="flex justify-end gap-2">
                        <Button variant="secondary" onClick={() => setShowPreferences(false)}>
                            {t('common.cancel')}
                        </Button>
                        <Button variant="primary" onClick={handleSavePreferences}>
                            {t('common.savePreferences')}
                        </Button>
                    </div>
                }
            >
                <div className="space-y-4">
                    <p className="text-sm text-neutral-600 dark:text-dark-textSecondary">
                        {t('cookies.preferencesDescription')}
                    </p>

                    <div className="space-y-4">
                        {/* Necessary */}
                        <div className="flex items-start justify-between">
                            <div>
                                <h4 className="font-medium text-neutral-900 dark:text-white">{t('cookies.necessary')}</h4>
                                <p className="text-xs text-neutral-500 dark:text-dark-textSecondary">
                                    {t('cookies.necessaryDescription')}
                                </p>
                            </div>
                            <input
                                type="checkbox"
                                checked={true}
                                disabled
                                className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 cursor-not-allowed opacity-50"
                            />
                        </div>

                        {/* Analytics */}
                        <div className="flex items-start justify-between">
                            <div>
                                <h4 className="font-medium text-neutral-900 dark:text-white">{t('cookies.analytics')}</h4>
                                <p className="text-xs text-neutral-500 dark:text-dark-textSecondary">
                                    {t('cookies.analyticsDescription')}
                                </p>
                            </div>
                            <input
                                type="checkbox"
                                checked={preferences.analytics}
                                onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                                className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                            />
                        </div>

                        {/* Marketing */}
                        <div className="flex items-start justify-between">
                            <div>
                                <h4 className="font-medium text-neutral-900 dark:text-white">{t('cookies.marketing')}</h4>
                                <p className="text-xs text-neutral-500 dark:text-dark-textSecondary">
                                    {t('cookies.marketingDescription')}
                                </p>
                            </div>
                            <input
                                type="checkbox"
                                checked={preferences.marketing}
                                onChange={(e) => setPreferences({ ...preferences, marketing: e.target.checked })}
                                className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                            />
                        </div>
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default CookieConsentBanner;
